import React, { useState } from 'react';

import { FormControl, MenuItem, Select } from '@mui/material';

import { ModalLayout } from 'layouts/ModalLayout/ModalLayout';
import { CustomButton } from 'components/CustomButton';
import styles from './SettlementModal.module.scss';

/**
 * Asks what actually happened to the money, before an invoice is marked paid
 * or put back.
 *
 * ## Why this is a dialog and not a dropdown
 *
 * The dropdown used to write `paymentStatus` straight to the invoice, so the
 * ledger recorded THAT an invoice was settled and never HOW. Cash received and
 * dues written off are the same row afterwards, which means the finance list
 * cannot be reconciled against a bank statement -- the two numbers differ by
 * every waiver, and nothing says which rows those were.
 *
 * Reopening is the more consequential direction and is why the reason is
 * required there: it puts a provider back in debt and can suspend them within
 * the hour, and "recorded against the wrong provider" and "the cheque bounced"
 * are very different conversations to then have with them.
 */

export type SettlementMethod = 'Manual' | 'Waived';

interface ISettlementModalProps {
  /** 'Paid' asks how it was settled; 'Not Paid' asks why it is being reopened. */
  intent: 'Paid' | 'Not Paid';
  /** For the summary line, so the admin can see they picked the right row. */
  providerName?: string;
  amount?: string;
  onConfirm: (payload: { settlementMethod?: SettlementMethod; note: string }) => void;
  onCancel: () => void;
}

export const SettlementModal = ({
  intent,
  providerName,
  amount,
  onConfirm,
  onCancel,
}: ISettlementModalProps) => {
  const isSettling = intent === 'Paid';

  const [method, setMethod] = useState<SettlementMethod>('Manual');
  const [note, setNote] = useState('');
  const [showReasonWarning, setShowReasonWarning] = useState(false);

  const handleConfirm = () => {
    // The server requires a reason to reopen (min 3 characters) and would
    // answer 400. Catching it here means the admin is told what is missing
    // rather than shown a generic failure.
    if (!isSettling && note.trim().length < 3) {
      setShowReasonWarning(true);
      return;
    }

    onConfirm({
      settlementMethod: isSettling ? method : undefined,
      note: note.trim(),
    });
  };

  return (
    <ModalLayout
      title={isSettling ? 'Record a payment' : 'Reopen this invoice'}
      handleClose={onCancel}
    >
      <div className={styles.body}>
        {(providerName || amount) && (
          <div className={styles.summary}>
            {providerName}
            {providerName && amount ? ' — ' : ''}
            {amount ? `${amount} EGP` : ''}
          </div>
        )}

        {isSettling ? (
          <>
            <div className={styles.label}>How was it settled?</div>
            <FormControl fullWidth>
              <Select
                data-testid="settlement-method"
                value={method}
                onChange={e => setMethod(e.target.value as SettlementMethod)}
                style={{ height: 44 }}
              >
                <MenuItem value="Manual">Cash or bank transfer received</MenuItem>
                <MenuItem value="Waived">Written off (waived)</MenuItem>
              </Select>
            </FormControl>
            <div className={styles.hint}>
              {method === 'Manual'
                ? 'The provider paid outside the app. This counts as income.'
                : 'The dues are cancelled. This does NOT count as income.'}
            </div>
          </>
        ) : (
          <div className={styles.hint}>
            The provider goes back to owing this invoice, and is suspended again
            if its grace period has already passed. An invoice paid by card
            cannot be reopened here — refund it in Paymob instead.
          </div>
        )}

        <div className={styles.label}>
          {isSettling ? 'Note (optional)' : 'Reason (required)'}
        </div>
        <textarea
          data-testid="settlement-note"
          className={styles.note}
          value={note}
          maxLength={500}
          onChange={e => {
            setNote(e.target.value);
            setShowReasonWarning(false);
          }}
          placeholder={
            isSettling
              ? 'e.g. bank transfer ref 88213'
              : 'e.g. recorded against the wrong provider'
          }
        />

        {showReasonWarning && (
          <div className={styles.warning}>
            Please say why this invoice is being reopened.
          </div>
        )}

        <div className={styles.actions}>
          <CustomButton title="Cancel" handleClick={onCancel} />
          <CustomButton
            title={isSettling ? 'Record payment' : 'Reopen invoice'}
            handleClick={handleConfirm}
            dark
          />
        </div>
      </div>
    </ModalLayout>
  );
};
